// In src/pages/RegistrationEventsPage.js

import React, { useState, useEffect } from 'react'; // 'useCallback' has been removed
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, doc, writeBatch, getDoc, serverTimestamp } from 'firebase/firestore';

import { 
  Container, Typography, CircularProgress, Box, Button, 
  Card, CardContent, CardActions, Grid, Chip 
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SettingsIcon from '@mui/icons-material/Settings';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/HourglassEmpty';

import format from 'date-fns/format';

function RegistrationEventsPage() {
  const { currentUser, userProfile } = useAuth();
  const [events, setEvents] = useState([]);
  const [churchMembers, setChurchMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRegisteringMap, setIsRegisteringMap] = useState({});
  const [userRegistrationStatusMap, setUserRegistrationStatusMap] = useState({});

  const isAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (userProfile?.churchId) {
      const usersColRef = collection(db, 'users');
      let usersQuery = query(usersColRef, where('churchId', '==', userProfile.churchId));

      const isCurrentViewerAdminOrSuperAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

      if (!isCurrentViewerAdminOrSuperAdmin) {
        usersQuery = query(usersQuery, where('role', '!=', 'Super Administrator'));
      }

      const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
        setChurchMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("Error fetching church members for POC:", error);
      });
      return () => unsubscribe();
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      setEvents([]);
      return;
    }

    let unsubscribeEvents = () => {};
    const churchDocRef = doc(db, 'churches', userProfile.churchId);
    const unsubscribeChurch = onSnapshot(churchDocRef, (docSnap) => {
      unsubscribeEvents(); 

      if (docSnap.exists() && docSnap.data().enableEventRegistration) {
        const eventsRef = collection(db, 'churches', userProfile.churchId, 'events');
        const q = query(eventsRef, where('isRegistrationEnabled', '==', true));

        unsubscribeEvents = onSnapshot(q, async (snapshot) => {
          const fetchedEvents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            startAt: doc.data().startAt?.toDate(),
            endAt: doc.data().endAt?.toDate(),
            registrationDeadline: doc.data().registrationDeadline?.toDate(),
          }));
          
          setEvents(fetchedEvents);

          const newUserRegistrationStatusMap = {};
          if (currentUser) {
            for (const event of fetchedEvents) {
              const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id, 'registrations', currentUser.uid);
              const docSnap = await getDoc(registrationDocRef);
              newUserRegistrationStatusMap[event.id] = docSnap.exists() ? docSnap.data() : null;
            }
          }
          setUserRegistrationStatusMap(newUserRegistrationStatusMap);
          setLoading(false);
        }, (error) => {
          console.error("Error fetching registration events:", error);
          setLoading(false);
        });

      } else {
        setEvents([]);
        setUserRegistrationStatusMap({});
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching church registration feature status:", error);
      setEvents([]);
      setUserRegistrationStatusMap({});
      setLoading(false);
    });

    return () => {
      unsubscribeChurch();
      unsubscribeEvents();
    };
  }, [userProfile, currentUser]);

  const formatDisplayDateTime = (date) => {
    if (!date) return 'N/A';
    return format(date, 'MMM do,PPPP h:mm a');
  };

  const getPOCNames = (pocUids) => {
    if (!pocUids || pocUids.length === 0) return 'N/A';
    return pocUids.map(uid => {
      const member = churchMembers.find(m => m.id === uid);
      return member ? `${member.firstName} ${member.lastName}` : uid;
    }).join(', ');
  };

  const handleRegisterNow = async (eventToRegister) => {
    if (!currentUser || !userProfile?.churchId || !eventToRegister?.id) {
      alert("You must be logged in and associated with a church to register.");
      return;
    }
    setIsRegisteringMap(prev => ({ ...prev, [eventToRegister.id]: true }));
    try {
      const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventToRegister.id);
      const eventDocSnap = await getDoc(eventDocRef);
      const currentEventData = eventDocSnap.data();

      if (!currentEventData.isRegistrationEnabled || !currentEventData.capacity) {
        alert("Registration is no longer enabled for this event or capacity is missing.");
        return;
      }
      const now = new Date();
      if (currentEventData.registrationDeadline && now > currentEventData.registrationDeadline.toDate()) {
        alert("Registration for this event has passed.");
        return;
      }
      if (currentEventData.currentRegistrations >= currentEventData.capacity) {
        alert("This event is full. No more registrations can be accepted.");
        return;
      }
      if (userRegistrationStatusMap[eventToRegister.id]) {
        alert("You are already registered for this event.");
        return;
      }
      const batch = writeBatch(db);
      const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventToRegister.id, 'registrations', currentUser.uid);
      batch.set(registrationDocRef, {
        userId: currentUser.uid,
        userName: `${userProfile.firstName} ${userProfile.lastName}`,
        registeredAt: serverTimestamp(),
        paymentStatus: currentEventData.isPaidEvent ? 'pending' : 'paid',
        ticketPrice: currentEventData.isPaidEvent ? currentEventData.ticketPrice : 0,
      });
      batch.update(eventDocRef, {
        currentRegistrations: currentEventData.currentRegistrations + 1,
      });
      await batch.commit();
      setUserRegistrationStatusMap(prev => ({ ...prev, [eventToRegister.id]: { userId: currentUser.uid, userName: `${userProfile.firstName} ${userProfile.lastName}`, registeredAt: serverTimestamp(), paymentStatus: currentEventData.isPaidEvent ? 'pending' : 'paid', ticketPrice: currentEventData.isPaidEvent ? currentEventData.ticketPrice : 0, }, }));
      alert("Successfully registered for the event!");
    } catch (error) {
      console.error("Error during registration:", error);
      alert(`Failed to register: ${error.message}`);
    } finally {
      setIsRegisteringMap(prev => ({ ...prev, [eventToRegister.id]: false }));
    }
  };

  const handleCancelRegistration = async (eventToCancel) => {
    if (!currentUser || !userProfile?.churchId || !eventToCancel?.id) return;
    
    const userRegStatus = userRegistrationStatusMap[eventToCancel.id];
    if (userRegStatus && userRegStatus.paymentStatus === 'paid') {
      alert("Cannot cancel registration: You have already paid for this event.");
      return;
    }

    if (window.confirm("Are you sure you want to cancel your registration for this event?")) {
      setIsRegisteringMap(prev => ({ ...prev, [eventToCancel.id]: true }));
      try {
        const batch = writeBatch(db);
        const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventToCancel.id);
        const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', eventToCancel.id, 'registrations', currentUser.uid);
        batch.delete(registrationDocRef);
        batch.update(eventDocRef, {
          currentRegistrations: eventToCancel.currentRegistrations - 1,
        });
        await batch.commit();
        setUserRegistrationStatusMap(prev => {
          const newState = { ...prev };
          delete newState[eventToCancel.id];
          return newState;
        });
        alert("Registration cancelled successfully.");
      } catch (error) {
        console.error("Error cancelling registration:", error);
        alert(`Failed to cancel registration: ${error.message}`);
      } finally {
        setIsRegisteringMap(prev => ({ ...prev, [eventToCancel.id]: false }));
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!events.length && !loading) {
    return (
      <Container>
        <Typography variant="h4" gutterBottom>Event Registrations</Typography>
        <Typography>No events requiring registration are currently available for your church, or the feature is not enabled by your Super Administrator.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>Event Registrations</Typography>
      
      <Grid container spacing={3}>
        {events.map((event) => {
          const userRegistration = userRegistrationStatusMap[event.id];
          const isRegistered = !!userRegistration;
          const isPaid = isRegistered && userRegistration.paymentStatus === 'paid';

          const isFull = event.currentRegistrations >= event.capacity;
          const isPastDeadline = event.registrationDeadline && new Date() > event.registrationDeadline;
          const isEventOver = new Date() > event.endAt;
          const isCurrentUserPOC = event.eventPOCs?.includes(currentUser?.uid) || false;

          let buttonContent;
          let buttonDisabled = false;
          let buttonAction;
          let buttonColor = "primary";

          if (isEventOver) {
            buttonContent = "Event Over";
            buttonDisabled = true;
          } else if (isPastDeadline) {
            buttonContent = "Registration Closed";
            buttonDisabled = true;
          } else if (isFull && !isRegistered) {
            buttonContent = "Event Full";
            buttonDisabled = true;
          } else if (isRegistered) {
            buttonContent = "Cancel Registration";
            buttonAction = () => handleCancelRegistration(event);
            buttonColor = "secondary";
            buttonDisabled = isRegisteringMap[event.id] || isPaid; 
          } else {
            buttonContent = "Register Now";
            buttonAction = () => handleRegisterNow(event);
            buttonColor = "primary";
            buttonDisabled = isRegisteringMap[event.id];
          }

          return (
            <Grid item key={event.id} xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography gutterBottom variant="h5" component="h2">{event.title}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        {isRegistered && <Chip label="Registered" color="success" size="small" icon={<CheckCircleOutlineIcon />} />}
                        {isPaid && <Chip label="Paid" color="primary" size="small" icon={<PaidIcon />} sx={{ mt: 0.5 }}/>}
                        {isRegistered && !isPaid && event.isPaidEvent && <Chip label="Payment Due" color="warning" size="small" icon={<PendingIcon />} sx={{ mt: 0.5 }}/>}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Starts: {formatDisplayDateTime(event.startAt)}
                  </Typography>
                  {event.location && (
                    <Typography variant="body2" color="text.secondary">Location: {event.location}</Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>{event.description}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Capacity: {event.currentRegistrations || 0} / {event.capacity}
                  </Typography>
                  {event.registrationDeadline && (
                    <Typography variant="body2" color="text.secondary">
                      Deadline: {formatDisplayDateTime(event.registrationDeadline)}
                    </Typography>
                  )}
                  {event.isPaidEvent && (
                    <Typography variant="body2" color="text.secondary">
                      Price: ${event.ticketPrice?.toFixed(2) || '0.00'}
                    </Typography>
                  )}
                  {event.eventPOCs && event.eventPOCs.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      POC(s): {getPOCNames(event.eventPOCs)}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button 
                    variant="contained" 
                    color={buttonColor} 
                    onClick={buttonAction} 
                    disabled={buttonDisabled}
                    startIcon={isRegisteringMap[event.id] ? <CircularProgress size={20} color="inherit" /> : <HowToRegIcon />}
                  >
                    {buttonContent}
                  </Button>
                  {(isAdmin || isCurrentUserPOC) && (
                    <Button 
                      variant="outlined" 
                      size="small"
                      startIcon={<SettingsIcon />}
                      component={RouterLink}
                      to={`/events/${event.id}/registrations`}
                    >
                      Manage
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
}

export default RegistrationEventsPage;